import MiniSearch from "minisearch";
import type { ExtensionType } from "@skills-marketplace/contracts/contract-3-marketplace-json";
import type { MarketplaceEntry } from "./catalog-entry";
import type { GitHubSource } from "@skills-marketplace/contracts/contract-1-sqs-envelope";
import type { SupportedClient } from "@skills-marketplace/contracts/contract-2-plugin-store";
import { usefulnessDisplay } from "./usefulness";
import { normaliseSignals, signalScore } from "./ranking";
// hybrid-retrieval imports only a TYPE from here (`ScoredEntry`), which is erased at compile
// time — so this is not a runtime circular import.
import { fuseRRF, indexByInstallName, weightsForQuery } from "./hybrid-retrieval";
import type { SemanticHit } from "./hybrid-retrieval";
import type { SimilarityCatalog } from "@skills-marketplace/contracts/contract-9-similarity";

/**
 * Official CAI allowlist test, injected by the caller. The default (`() => false`) makes
 * `officialOnly` empty and the `official` sort a no-op.
 */
export type IsOfficial = (p: MarketplaceEntry | null | undefined) => boolean;

/**
 * Catalog orderings. Declared here (rather than beside the dropdown) so the union
 * has one home — it used to be spelled out separately in SearchOptions, sortPlugins
 * and the toolbar, which meant adding an ordering required widening three unions and
 * silently half-worked if you missed one.
 *
 * "relevance" is the odd one out: it is not a comparator but the absence of one,
 * meaning "keep the order the search engine returned". See filterPlugins.
 */
export type SortBy = "relevance" | "mostUsed" | "topRated" | "name" | "updated" | "created" | "official";

/** The orderings sortPlugins can actually compare — everything except "relevance". */
export type ComparatorSortBy = Exclude<SortBy, "relevance">;

export interface SearchOptions {
  query: string;
  supportedClients?: SupportedClient[];
  categories?: string[];
  extensionType?: ExtensionType;
  /** Narrow to entries on the curated Official CAI allowlist. */
  officialOnly?: boolean;
  sortBy?: SortBy;
  sortOrder?: "asc" | "desc";
  /**
   * How multi-term queries combine. Defaults to "strict" (today's AND) so any
   * caller that omits it is unaffected. See SearchMode.
   */
  mode?: SearchMode;
  /**
   * Blend adoption + votes into search result ordering.
   *
   * NO PRODUCTION CALLER — measurement only, like `boosts` and `rrfWeights` below.
   *
   * This shipped briefly as a "Smart ranking" toggle and was REMOVED from the UI. Signals are a
   * property of an entry, not of its relevance to a query, and mixing them into the ranking was
   * measured to be the wrong mechanism twice over:
   *
   *   - over RRF it degraded P@1 monotonically (52% -> 43% across weights 0.05-0.5) AND moved the
   *     eight `signal-decides` cases by ZERO at every weight. A multiplier is query-independent,
   *     but whether the user cares about popularity is query-dependent.
   *   - the same data reaching a model as prose FACTS moved those cases 50% -> 75%.
   *
   * So signals now have three legible homes instead: an explicit sort (browse — you asked for it,
   * and there is no relevance to compete with), the card display, and the AI-mode prompt. See
   * docs/v2/skill-discovery-v1.md 4.6.
   *
   * Kept rather than deleted because compare-ranking.ts and compare-dedupe.ts still reproduce the
   * Part 3 measurement through it; removing the option would delete re-runnable evidence.
   */
  useSignals?: boolean;
  /**
   * `installName` → identity-cluster id, from Contract 9's `kind: "identity"`
   * clusters. When provided, results are collapsed so one card stands for the
   * whole cluster. Omit (or pass an empty map) to disable — an absent similarity
   * artifact must leave results exactly as they are today.
   */
  duplicateGroups?: Map<string, string>;
  /**
   * Semantic hits for this query, from the embedding artifact, to be FUSED with the lexical
   * results into a single ranking.
   *
   * WHY ONE RANKING RATHER THAN APPENDING. An earlier revision appended semantic-only hits
   * below the lexical ones, on the argument that fusion cost 5 points of P@1 (57% → 52%). On
   * 41 gradeable cases that difference is about TWO cases — inside the noise of a hand-built
   * golden set — and it was paid for with a list that is two rankings stapled together:
   * positions 1..N ordered by BM25, N+1.. by cosine. Nobody can reason about that order, and
   * in practice it kept a weak lexical hit above a much better semantic one (`deployment
   * summary` put `observability` above `probe-ship-digest`). Coherent ordering is worth more
   * than two cases of P@1.
   *
   * Fused here — after search, before filtering — so the active client/category/type filters
   * and duplicate collapsing apply to the whole ranking. `sortBy: "relevance"` keeps the fused
   * order; any other sortBy reorders it.
   *
   * Absent (backend down, still loading, no local server) this falls through to lexical-only
   * search, which is a correct and complete result — just with lower recall.
   */
  semanticHits?: SemanticHit[];
  /** Override per-field weights, for measurement only. See FIELD_BOOSTS. */
  boosts?: FieldBoosts;
  /**
   * Override the RRF lexical:semantic weighting, for measurement only.
   *
   * Production uses DEFAULT_WEIGHTS. Exists so a sweep can measure weightings through THIS
   * function rather than by calling fuseRRF directly — measuring the raw fusion misses
   * everything that happens after it (exact-name promotion, the rejection floor, filters,
   * duplicate collapsing), and the field-boost sweep showed that difference is large enough to
   * change the conclusion.
   */
  rrfWeights?: { lexical: number; semantic: number };
}

/**
 * `as const` rather than `(keyof MarketplaceEntry)[]` on purpose: the literal element types are
 * what let FIELD_BOOSTS' `satisfies` require an entry for every indexed field. Typed as the
 * wider array, the constraint silently expanded to all ~40 keys of MarketplaceEntry and stopped
 * catching anything — which is how five fields ended up indexed with no deliberate weight.
 */
const INDEXED_FIELDS = [
  "pluginName", "displayName", "description",
  "orgOrOwner", "repoName", "author",
  "installName", "skillPath",
] as const satisfies readonly (keyof MarketplaceEntry)[];

/**
 * Array-valued fields flattened into searchable text (#626). Kept separate from
 * INDEXED_FIELDS because each needs its own extraction in `extractField`.
 *
 * `_categoriesText` closes a measured gap: `categories` is populated on 100% of
 * catalog entries — the only field that is — and was not searched at all, while
 * `keywords` carried a 2x boost on just 22% coverage.
 *
 * `_skillNamesText` makes a plugin findable by the name of a skill it contains.
 * BM25's field-length normalisation keeps this from becoming a flood: a plugin with
 * 197 skill names has a very long field, so each term contributes proportionally
 * less than the same term in a short one.
 */
const SYNTHETIC_FIELDS = ["_keywordsText", "_categoriesText", "_skillNamesText"] as const;

/**
 * Separator the catalog builder uses for a synthesized per-skill entry:
 * `${parentManifestName}--skills--${skillSlug}`.
 */
const SKILLS_INFIX = "--skills--";

/**
 * The searchable part of an installName — the skill's own slug for a synthesized
 * entry, the whole value otherwise.
 *
 * Measured problem (#626 Part 0): every one of a plugin's synthesized children
 * embeds the PARENT's name in its installName, and MiniSearch tokenises on
 * non-alphanumerics — so `observability` appeared as a token in all 197
 * `observability--skills--*` entries and querying the plugin name matched its entire
 * family by construction (217 results, 198 from one artifact). Those children have
 * empty keywords and unrelated descriptions; the flood came purely from indexing a
 * synthetic identifier as if it were prose.
 *
 * Trimming the prefix rather than dropping the field keeps the tested "search by
 * installName" behaviour working — `…--skills--cai-discovery` still matches
 * `cai-discovery`, because that segment is what gets indexed.
 */
function indexableInstallName(installName: string): string {
  const i = installName.lastIndexOf(SKILLS_INFIX);
  return i === -1 ? installName : installName.slice(i + SKILLS_INFIX.length);
}

/** A synthesized per-skill entry, as opposed to a record published in its own right. */
function isSynthesizedSkill(entry: MarketplaceEntry): boolean {
  return !!entry.installName?.includes(SKILLS_INFIX);
}

/**
 * The searchable part of `skillPath` — empty for a synthesized child.
 *
 * This, not installName, turned out to be the main driver of the measured fan-out.
 * `synthesizeSkillEntries` COPIES the parent's skillPath onto every child (Contract 7
 * documents this: sub-skills carry their parent's skillPath so download paths
 * resolve), so all 197 `observability` children carry `plugins/observability`
 * verbatim. Indexing it made a query for the plugin name match its entire family —
 * and trimming to the last path segment does not help, because the last segment IS
 * the parent's name.
 *
 * For a synthesized child the value describes its PARENT, not itself, so it carries
 * no information about the thing being searched for. For a genuine multi-manifest
 * entry, skillPath is that artifact's own identity, so it stays indexed — which is
 * what keeps the "searches by skillPath" behaviour working.
 */
function indexableSkillPath(entry: MarketplaceEntry): string {
  if (isSynthesizedSkill(entry)) return "";
  return entry.skillPath ?? "";
}

/*
 * NO STEMMING — tried, measured, reverted (#626).
 *
 * A hand-rolled conservative suffix normaliser (plural -s, -ing, -ed, -ment, with
 * minimum-length guards) was wired in via MiniSearch's `processTerm`. It did fix the
 * cases Part 0 flagged — `deployment`/`deploy`, `reviews`/`review`,
 * `testing`/`test` — but measurement showed it was a net loss:
 *
 *   - It CREATED a false-match family. Stemming shortens tokens, which pulls more
 *     pairs inside the `fuzzy: 0.2` edit budget. `previews` (in the repo name
 *     `scratchpad-previews-repo`) normalised to `preview`, putting it 1 edit from
 *     `review` — so a query for `review` matched all 44 entries in that repo.
 *     Measured: `review` 179 → 223 results, of which 44 were spurious; `pr review`
 *     50 → 94. Unstemmed, those repos matched 0.
 *   - It still under-stems where it matters. `logs`/`log` stayed separate (below the
 *     length guard), and `summary`/`summarises` reduce to different roots regardless.
 *
 * The lesson is that stemming and fuzzy matching interact: adding one requires
 * retuning the other. The correct fix is a real stemmer (Porter/Snowball via a
 * library, as MiniSearch's own docs recommend) introduced together with a lowered
 * fuzzy budget and measured against a golden query set — a deliberate change, not a
 * drive-by. Do not re-add a hand-rolled version.
 */

function pluginId(p: MarketplaceEntry): string {
  if (p.installName) return p.installName;
  const base = `${p.source}--${p.orgOrOwner}--${p.repoName}`;
  return p.skillPath ? `${base}--${p.skillPath}` : base;
}

interface SearchIndex {
  mini: MiniSearch<MarketplaceEntry>;
  idToPlugins: Map<string, MarketplaceEntry[]>;
}

function buildIndex(plugins: MarketplaceEntry[], boostOverride?: FieldBoosts): SearchIndex {
  const boosts = { ...FIELD_BOOSTS, ...boostOverride };
  const ids = new Map<MarketplaceEntry, string>();
  // Dedupe documents by id before indexing. MiniSearch.addAll throws on a
  // duplicate id, and a single malformed catalog record (e.g. two synthesized
  // skills that collide on installName) would otherwise crash search for every
  // plugin. idToPlugins maps every original entry to its id, so searchPlugins
  // returns all entries sharing a matched id — only the redundant index doc is
  // dropped. Defense-in-depth: the catalog should not emit duplicate ids.
  const seen = new Set<string>();
  const docs: (MarketplaceEntry & { _searchId: string })[] = [];
  for (const p of plugins) {
    const id = pluginId(p);
    ids.set(p, id);
    if (seen.has(id)) {
      // Matches the house convention for malformed-catalog signals (see
      // ClientBadge / parseHash): warn once per duplicate so a broken catalog
      // publish leaves a client-side breadcrumb. buildIndex is memoized per
      // catalog array (indexCache), so this does not fire per keystroke.
      console.warn(
        `[search] Duplicate catalog id "${id}" (displayName="${p.displayName}") — ` +
          "dropping redundant index doc. The catalog should not emit duplicate " +
          "installNames; run the reprocess script to investigate.",
      );
      continue;
    }
    seen.add(id);
    docs.push({ ...p, _searchId: id });
  }

  const mini = new MiniSearch<MarketplaceEntry>({
    idField: "_searchId",
    fields: [...INDEXED_FIELDS, ...SYNTHETIC_FIELDS],
    // No processTerm override — MiniSearch's default (lowercase) only. See the
    // "NO STEMMING" note above for what was tried and why it was reverted.
    extractField: (doc, field) => {
      if (field === "_searchId") return (doc as unknown as { _searchId: string })._searchId;
      const entry = doc as MarketplaceEntry;
      if (field === "_keywordsText") return entry.keywords?.join(" ") ?? "";
      if (field === "_categoriesText") return entry.categories?.join(" ") ?? "";
      if (field === "_skillNamesText") {
        return entry.skills?.map((s) => s.name).filter(Boolean).join(" ") ?? "";
      }
      // Both of these carry the PARENT's identity on a synthesized child, so they are
      // trimmed/suppressed rather than indexed as prose. See their doc comments.
      if (field === "installName") return entry.installName ? indexableInstallName(entry.installName) : "";
      if (field === "skillPath") return indexableSkillPath(entry);
      return (doc as unknown as Record<string, string>)[field] ?? "";
    },
    searchOptions: {
      boost: boosts,
      prefix: (_term, i, terms) => i === terms.length - 1,
      fuzzy: 0.2,
      combineWith: "AND",
    },
  });

  mini.addAll(docs);

  const idToPlugins = new Map<string, MarketplaceEntry[]>();
  for (const p of plugins) {
    const id = ids.get(p)!;
    const bucket = idToPlugins.get(id);
    if (bucket) bucket.push(p);
    else idToPlugins.set(id, [p]);
  }

  return { mini, idToPlugins };
}

/**
 * Per-field weight for EVERY indexed field.
 *
 * Every field the index contains has an explicit entry here, deliberately — the `satisfies`
 * below makes an omission a compile error. Five fields (`orgOrOwner`, `repoName`, `author`,
 * `installName`, `skillPath`) previously had none, so MiniSearch silently defaulted them to 1,
 * the same weight as `description`. That was an accident of omission, not a decision: a
 * machine-generated mount path was competing equally with human prose.
 *
 * Ordering rationale, highest to lowest:
 *
 *   3     pluginName, displayName   the entry's own identity — what someone types when they know
 *                                   what they want. Exactness on top of this is handled by
 *                                   promoteExactNameMatches, because BM25 cannot be trusted to
 *                                   rank an exact name first (it ranked `acc` above `acc.run`).
 *   2     keywords                  author-curated, deliberately chosen terms. ~22% populated, so
 *                                   it helps where present and is inert elsewhere.
 *   1.5   skill names               a plugin should be findable by a skill it contains, but must
 *                                   not outrank that skill's own card.
 *   1     description               MEASURED. It looks wrong that a list of contained names
 *                                   outranks the text saying what the thing DOES, so this was
 *                                   raised to 1.5 — and the sweep showed it COSTS recall
 *                                   (76% → 74%) while changing no P@1 in any category. Raising
 *                                   it further (2, 3) is no better. Description is long prose,
 *                                   so BM25's length normalisation already discounts each term;
 *                                   boosting the field amplifies incidental word matches more
 *                                   than intentional ones. Left at 1 on the evidence, against
 *                                   intuition.
 *   1     repoName, orgOrOwner      people do search by repo and team, and both are short and
 *                                   high-precision — but neither is what the entry IS.
 *   0.75  author                    rarely populated, rarely searched.
 *   0.5   categories                coarse ("Engineering", "DevOps") and shared by hundreds of
 *                                   entries; should help a category-shaped query resolve without
 *                                   competing with prose.
 *   0.5   installName, skillPath    machine-generated mount paths (`acc--skills--acc-run`). They
 *                                   must stay searchable — people paste them — but they repeat
 *                                   the name tokens already scored at 3, so a full weight
 *                                   double-counts identity and buries prose matches.
 *
 * WHAT THE SWEEP ACTUALLY FOUND (sweep-field-boosts.ts, through the full pipeline):
 * across 14 single-field variants, every P@1 figure in every category was IDENTICAL, and only
 * `description` moved anything at all — downward. Field weights are close to inert for these 42
 * queries, and `exact-name` is 100% in every row because promoteExactNameMatches now decides it
 * instead of the boosts.
 *
 * So the value of this table is intent and visibility, not tuning. Where evidence exists it is
 * followed (description stays at 1). Where none does, the number is reasoned and labelled as
 * unvalidated rather than left to an invisible default.
 *
 * CAVEAT worth keeping in mind before trusting the "inert" conclusion: the golden set contains no
 * query that searches by ORG, REPO or AUTHOR, so those three rows could not have moved. That is a
 * coverage gap in the golden set, not evidence that those weights do not matter.
 */
export const FIELD_BOOSTS = {
  pluginName: 3,
  displayName: 3,
  _keywordsText: 2,
  _skillNamesText: 1.5,
  description: 1,
  repoName: 1,
  orgOrOwner: 1,
  author: 0.75,
  _categoriesText: 0.5,
  installName: 0.5,
  skillPath: 0.5,
} satisfies Record<
  (typeof INDEXED_FIELDS)[number] | (typeof SYNTHETIC_FIELDS)[number],
  number
>;

/** Boost overrides, for measuring alternatives against the golden set. */
export type FieldBoosts = Partial<Record<keyof typeof FIELD_BOOSTS, number>>;

/**
 * Keyed on catalog ARRAY IDENTITY. A sweep can therefore force a rebuild with different boosts
 * by passing a fresh copy (`[...catalog]`), with no cache-invalidation machinery.
 */
const indexCache = new WeakMap<MarketplaceEntry[], SearchIndex>();

function getIndex(plugins: MarketplaceEntry[], boosts?: FieldBoosts): SearchIndex {
  let cached = indexCache.get(plugins);
  if (!cached) {
    cached = buildIndex(plugins, boosts);
    indexCache.set(plugins, cached);
  }
  return cached;
}

/**
 * How multi-term queries are combined (issue #626 Part 2).
 *
 * - "strict"  — every query term must match (`combineWith: "AND"`). Today's
 *               behaviour and still the default, so existing callers are
 *               unaffected.
 * - "relaxed" — terms are OR'd, then results are ordered by how many query
 *               terms each entry matched before relevance.
 *
 * Why relaxed is not a precision/recall trade-off
 * ───────────────────────────────────────────────
 * Anything strict would have returned matched ALL the terms, so it lands in the
 * highest coverage bucket and keeps its position. Everything strict silently
 * DISCARDED is appended below, in relevance order. The head is unchanged; only
 * the tail grows. So relaxed cannot rank a worse result above a better one than
 * strict did — it can only stop returning nothing.
 *
 * Measured on the 2026-08-13 non-prod catalog (docs/v2/skill-discovery-poc-plan.md):
 * `troubleshoot` matched 30 entries and `errors` matched 30, but `troubleshoot
 * errors` matched ZERO under strict, because no single entry contained both. The
 * catalog holds 24 debugging skills the user could not reach.
 */
export type SearchMode = "strict" | "relaxed" | "auto";

/**
 * Mirrors MiniSearch 7's default tokenizer (`SPACE_OR_PUNCTUATION` in its source)
 * so a query term count here matches what the index actually searched for.
 * Replicated rather than imported because MiniSearch does not export it.
 */
const QUERY_TOKEN_SPLIT = /[\n\r\p{Z}\p{P}]+/u;

function countQueryTerms(query: string): number {
  return new Set(query.toLowerCase().split(QUERY_TOKEN_SPLIT).filter(Boolean)).size;
}

/**
 * Relaxed mode keeps a partial-coverage result only if it scores at least this
 * fraction of the best hit's score. Full-coverage results are NEVER subject to it.
 *
 * Why a relative score floor rather than a minimum-coverage floor
 * ──────────────────────────────────────────────────────────────
 * Measured on the 1,185-entry non-prod catalog, the OR tail is overwhelmingly
 * 1-term matches — `improve code quality` returned 1 full-coverage hit, 38 at two
 * terms and 300 at one. A coverage floor would trim that beautifully, but
 * `troubleshoot errors` and `accessibility audit` are 100% single-term matches, so
 * the same rule takes them back to zero and undoes the entire point.
 *
 * A relative score floor self-calibrates per query where a fixed coverage rule
 * cannot, so it controls VOLUME well: measured at 10%, 339 → 38 and 340 → 83,
 * while the two zero-result queries still return 5 and 22.
 *
 * KNOWN LIMITATION — this fixes volume, not ordering
 * ──────────────────────────────────────────────────
 * What *should* separate a useful single-term match from a noisy one is term
 * rarity: matching only "accessibility" (3 entries) is a great result, matching
 * only "audit" (49) is not. BM25 prices rarity via IDF, but the field boosts above
 * (pluginName/displayName 3x) overwhelm it — a common term in a NAME beats a rare
 * term in a DESCRIPTION. Measured for `accessibility audit`: "audit"-in-name hits
 * score 52–64 while the two "accessibility"-in-description hits score 7.6 and 6.6,
 * landing at ranks 21 and 22 of 22. Every result a user sees is about auditing and
 * none is about accessibility.
 *
 * So relaxed mode makes previously-unreachable entries reachable, which is its
 * purpose, but does not by itself put the right one first. Rebalancing boosts
 * against IDF belongs with the ranking work (#626 Part 3), not here.
 */
const RELAXED_TAIL_SCORE_FLOOR = 0.1;

/**
 * Minimum best-cosine for a query to be considered answerable at all (#626).
 *
 * See the rejection block in `filterPlugins` for the measured separation. Exported so the
 * measurement script and any future Lambda use the same number rather than two that drift.
 *
 * NO TAIL CUT accompanies this, deliberately. Trimming results whose cosine falls below a
 * fraction of the best was measured and REJECTED: at 0.90 it dropped 13 of 42 correct answers,
 * including `probe-ship-digest` for `deployment summary` (cosine 0.282 against a top of 0.326,
 * a ratio of 0.87). Relevance falloff in cosine terms simply does not align with where the
 * right answers sit. The long tail is handled the way web search handles it — pagination
 * (PAGE_SIZE, infinite scroll) — rather than truncation.
 */
export const SEMANTIC_REJECT_FLOOR = 0.25;

/**
 * Reduce a name to a comparison key: lowercase, alphanumerics only.
 *
 * Collapses every separator convention the catalog uses for one name — `acc.run`, `acc-run`,
 * `acc run` and `ACC.Run` all become `accrun` — so typing a skill's name in any plausible form
 * counts as having typed it exactly.
 */
function nameKey(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/**
 * Move entries whose NAME is exactly what the user typed to the front (#626).
 *
 * WHY THIS IS NEEDED. BM25 scores term density, not exactness, and its field-length
 * normalisation actively penalises the longer name. Measured on the real catalog, `acc.run`
 * returned:
 *
 *   1. 249.90  acc          ← a different, shorter skill
 *   2. 247.59  acc.run      ← what the user typed
 *   3. 152.83  acc.signoff
 *
 * `acc` wins because its one-token name makes the match maximally dense, and because as the
 * parent plugin it also contains "run" through its child skills. The exact match loses by 2.3
 * points out of 250 for being two tokens instead of one. Boost tuning cannot fix that
 * reliably: this is a near-tie broken the wrong way by a length heuristic unrelated to what the
 * user asked for, and shifting scores just moves which pair ties.
 *
 * So exactness is PRECEDENCE, not score. If you typed a name, that entry is first; everything
 * else keeps its relative order behind it. Deterministic and explainable, which is what "I
 * typed its name" deserves.
 *
 * Applied AFTER fusion so semantic evidence cannot undo it, and BEFORE duplicate collapsing so
 * the exact match is the copy that survives its identity cluster.
 *
 * Only WHOLE-name equality counts. A prefix (`acc` for `acc.run`) or a substring does not —
 * those are ordinary relevance, and promoting them would recreate this bug with new victims.
 */
export function promoteExactNameMatches(
  results: MarketplaceEntry[],
  query: string,
): MarketplaceEntry[] {
  const key = nameKey(query);
  if (key === "") return results;

  const isExact = (e: MarketplaceEntry): boolean => {
    if (nameKey(e.pluginName ?? "") === key) return true;
    // installName is a mount path (`acc--skills--acc-run`); its final segment is the skill's
    // own name, which is the part a user would type.
    const install = e.installName;
    if (install) {
      const i = install.lastIndexOf(SKILLS_INFIX);
      const own = i === -1 ? install : install.slice(i + SKILLS_INFIX.length);
      if (nameKey(own) === key) return true;
    }
    return false;
  };

  const exact: MarketplaceEntry[] = [];
  const rest: MarketplaceEntry[] = [];
  for (const e of results) (isExact(e) ? exact : rest).push(e);
  // No exact match is the common case; return the original array untouched.
  return exact.length === 0 ? results : [...exact, ...rest];
}

/** A search hit with the signals that ranked it, for blending and for diagnostics. */
export interface ScoredEntry {
  entry: MarketplaceEntry;
  /**
   * Count of DISTINCT query terms this entry matched. MiniSearch reports these
   * as `queryTerms` — the terms as the user typed them, so a fuzzy or prefix
   * expansion ("moto" matching "motorcycle") still counts as its query term and
   * is not double-counted.
   */
  covered: number;
  /**
   * Raw MiniSearch relevance. Unbounded and query-dependent — normalise per
   * result set before combining it with anything else.
   */
  score: number;
}

export interface SearchTuning {
  /** Defaults to "strict" — today's behaviour, so an omitting caller is unaffected. */
  mode?: SearchMode;
  /**
   * Override per-field weights, for measuring alternatives against the golden set.
   *
   * Production never passes this — FIELD_BOOSTS is the shipped configuration. The index is
   * cached by array identity, so a sweep must pass a fresh copy of the catalog alongside this
   * or it will get back an index built with the previous boosts.
   */
  boosts?: FieldBoosts;
  /**
   * Blend adoption + votes into the ordering (#626 Part 3). Off by default: this is
   * the behaviour change #262 asks for, and it should be opted into explicitly
   * rather than altering every existing caller's result order.
   */
  useSignals?: boolean;
}

/**
 * Weight on the normalised quality signal when blending with relevance:
 *
 *   final = normalisedRelevance × (1 + SIGNAL_WEIGHT × normalisedSignal)
 *
 * MULTIPLICATIVE, not additive, and this matters. Additive blending lets a very
 * popular but barely-relevant entry outrank a precise match — which the measured
 * fan-out would amplify badly, since one plugin can contribute ~200 entries that
 * all inherit high usage. Multiplying keeps relevance as the gate and lets the
 * signal reorder within comparable relevance, so a signal can promote a good match
 * over an equally good match but can never rescue a bad one.
 *
 * At 0.5 a maximally-used entry gets at most a 1.5x lift — enough to reorder
 * near-ties, not enough to leapfrog a materially better match.
 */
const SIGNAL_WEIGHT = 0.5;

export function searchPluginsScored(
  plugins: MarketplaceEntry[],
  query: string,
  tuning: SearchTuning = {},
): ScoredEntry[] {
  const { mode = "strict", useSignals = false, boosts } = tuning;
  if (!query.trim()) return plugins.map((entry) => ({ entry, covered: 0, score: 0 }));

  // "auto": strict first, broaden ONLY on an empty result set.
  //
  // Measured, neither mode wins outright. On the probed test catalog `accessibility
  // audit` strict returned exactly one result — the correct one — where relaxed also
  // surfaces "audit"-flavoured distractions. (The 1,185-entry pre-probe catalog cited in
  // the RELAXED_TAIL/KNOWN-LIMITATION notes above had NO single entry matching both
  // terms, so there strict returned zero and the correct hits landed deep — same design
  // point, different catalog snapshot.) For `deployment summary` strict returned nothing
  // and relaxed returned results. So relaxed is an improvement only where strict has
  // nothing to offer, and always-on relaxed pays a precision
  // cost on the queries that already worked. Broadening on empty gets the rescue
  // without the cost, and needs no user-facing toggle to decide it.
  if (mode === "auto") {
    // Forward EVERY tuning option to the recursive calls, not just the ones in use today.
    // Spreading `tuning` means a new option cannot be silently dropped here — `boosts` was,
    // and because "auto" is what the SPA and every measurement script use, a boost sweep
    // produced fourteen identical rows and would have "proved" that field weights do not
    // matter. Listing options by hand is how that happens.
    const strictResults = searchPluginsScored(plugins, query, { ...tuning, mode: "strict" });
    if (strictResults.length > 0) return strictResults;
    return searchPluginsScored(plugins, query, { ...tuning, mode: "relaxed" });
  }

  const { mini, idToPlugins } = getIndex(plugins, boosts);
  // Per-search options are merged over the constructor's (MiniSearch
  // `{ ...this._options.searchOptions, ...searchOptions }`), so the field boosts,
  // prefix rule and fuzziness configured in buildIndex all still apply here.
  const hits = mode === "relaxed"
    ? mini.search(query, { combineWith: "OR" })
    : mini.search(query);

  let scored = hits.map((h) => ({
    h,
    covered: new Set(h.queryTerms).size,
  }));

  if (mode === "relaxed") {
    // Trim the partial-coverage tail, never the full-coverage head. A
    // full-coverage hit is by definition one strict would have returned, so
    // exempting it makes `relaxed ⊇ strict` true by construction rather than by
    // observation — without this, a low-scoring full-coverage hit could be
    // trimmed and relaxed would LOSE results strict returned (measured: `code
    // review` dropped 92 → 83).
    const totalTerms = countQueryTerms(query);
    const topScore = Math.max(...scored.map((s) => s.h.score), 0);
    const floor = topScore * RELAXED_TAIL_SCORE_FLOOR;
    scored = scored.filter((s) => s.covered >= totalTerms || s.h.score >= floor);

    // Coverage first, relevance second. Under strict every hit covers every term,
    // so applying this ordering there would be a no-op — it is skipped only to
    // leave the existing code path untouched.
    scored.sort((a, b) => b.covered - a.covered || b.h.score - a.h.score);
  }

  // Flatten to entries BEFORE blending: one index doc can map to several entries
  // (idToPlugins), and a quality signal belongs to an entry, not to a hit.
  const flat: ScoredEntry[] = scored.flatMap(({ h, covered }) =>
    (idToPlugins.get(h.id as string) ?? []).map((entry) => ({ entry, covered, score: h.score })),
  );

  if (!useSignals || flat.length === 0) return flat;

  // Both terms are normalised across THIS result set so they share a scale.
  // MiniSearch scores are unbounded and query-dependent, so skipping this would let
  // whichever term happens to have the wider range dominate for arbitrary reasons.
  const topRelevance = Math.max(...flat.map((s) => s.score), 0);
  const normalisedSignals = normaliseSignals(flat.map((s) => signalScore(s.entry).score));

  const blended = flat.map((s, i) => ({
    s,
    final: (topRelevance > 0 ? s.score / topRelevance : 0) * (1 + SIGNAL_WEIGHT * normalisedSignals[i]),
  }));

  // Coverage still gates; the blended score only reorders WITHIN a coverage tier.
  // A two-word match must keep beating a one-word match however popular the latter
  // is — that is what puts the right answer first for `accessibility audit`, and a
  // signal strong enough to break it would undo Part 2's main win.
  blended.sort((a, b) => b.s.covered - a.s.covered || b.final - a.final);
  return blended.map(({ s }) => s);
}

export function searchPlugins(
  plugins: MarketplaceEntry[],
  query: string,
  tuning: SearchTuning = {},
): MarketplaceEntry[] {
  if (!query.trim()) return plugins;
  return searchPluginsScored(plugins, query, tuning).map((s) => s.entry);
}

/**
 * Filter plugins by source
 */
export function filterBySource(plugins: MarketplaceEntry[], source: GitHubSource): MarketplaceEntry[] {
  return plugins.filter((p) => p.source === source);
}

/**
 * Filter plugins by supported client
 */
export function filterBySupportedClient(
  plugins: MarketplaceEntry[],
  client: SupportedClient,
): MarketplaceEntry[] {
  return plugins.filter((p) => {
    const clients = p.supportedClients && p.supportedClients.length > 0
      ? p.supportedClients
      : ["claude-code"];
    return clients.includes(client);
  });
}

/**
 * Filter plugins by multiple supported clients (OR — must support at least one)
 */
export function filterBySupportedClients(
  plugins: MarketplaceEntry[],
  clients: SupportedClient[],
): MarketplaceEntry[] {
  return plugins.filter((p) => {
    const pluginClients = p.supportedClients && p.supportedClients.length > 0
      ? p.supportedClients
      : ["claude-code"];
    return clients.some((c) => pluginClients.includes(c));
  });
}

/**
 * Filter plugins by skill type
 */
export function filterBySkillType(
  plugins: MarketplaceEntry[],
  skillType: "claude-code" | "claude-cowork"
): MarketplaceEntry[] {
  return plugins.filter((p) => p.skillType === skillType);
}

/**
 * Filter plugins by extension type (plugin vs skill).
 *
 * Strict equality on purpose: an entry whose extensionType is absent/unknown
 * (e.g. a record written before schema 1.3, which predates the field) matches
 * neither "plugin" nor "skill", so it is excluded from those facets but never
 * surfaces under one it doesn't belong to. Such entries stay visible only when
 * no type filter is set.
 */
export function filterByExtensionType(
  plugins: MarketplaceEntry[],
  extensionType: ExtensionType,
): MarketplaceEntry[] {
  return plugins.filter((p) => p.extensionType === extensionType);
}

/**
 * Filter plugins by role category
 */
export function filterByCategory(
  plugins: MarketplaceEntry[],
  category: string,
): MarketplaceEntry[] {
  return plugins.filter((p) =>
    p.categories && p.categories.some(
      (c) => c.toLowerCase() === category.toLowerCase()
    )
  );
}

/**
 * Filter plugins by multiple categories (OR — must match at least one)
 */
export function filterByCategories(
  plugins: MarketplaceEntry[],
  categories: string[],
): MarketplaceEntry[] {
  return plugins.filter((p) =>
    p.categories && p.categories.some(
      (c) => categories.some((cat) => cat.toLowerCase() === c.toLowerCase())
    )
  );
}

/**
 * Filter plugins by keywords (OR — must have at least one)
 */
export function filterByKeywords(
  plugins: MarketplaceEntry[],
  keywords: string[],
): MarketplaceEntry[] {
  return plugins.filter((p) => {
    if (!p.keywords) return false;
    const lower = p.keywords.map((k) => k.toLowerCase());
    return keywords.some((kw) => lower.includes(kw.toLowerCase()));
  });
}

/**
 * Extract unique, sorted categories from a plugin list
 */
export function extractCategories(plugins: MarketplaceEntry[]): string[] {
  const seen = new Map<string, string>();
  for (const plugin of plugins) {
    if (plugin.categories) {
      for (const cat of plugin.categories) {
        const trimmed = cat.trim();
        if (trimmed && !seen.has(trimmed.toLowerCase())) {
          seen.set(trimmed.toLowerCase(), trimmed);
        }
      }
    }
  }
  return Array.from(seen.values()).sort((a, b) =>
    a.toLowerCase().localeCompare(b.toLowerCase())
  );
}

/**
 * Sort plugins
 */
export function sortPlugins(
  plugins: MarketplaceEntry[],
  sortBy: ComparatorSortBy = "name",
  sortOrder: "asc" | "desc" = "asc",
  isOfficial: IsOfficial = () => false,
): MarketplaceEntry[] {
  const sorted = [...plugins];
  const multiplier = sortOrder === "asc" ? 1 : -1;

  sorted.sort((a, b) => {
    let aValue: string | number;
    let bValue: string | number;

    switch (sortBy) {
      case "official": {
        // Official entries first, and NOTHING else: within each group the incoming
        // order is preserved (Array.prototype.sort is stable, ES2019+). sortOrder is
        // irrelevant — "Official CAI first" is a fixed ordering.
        //
        // This deliberately has no name tiebreak. "Official first, then by name" reads
        // fine until you sort a set that contains no official entries at all, at which
        // point the tiebreak IS the sort and the control quietly turns into an
        // alphabetical sort. Searching "pr review" against the real catalog does exactly
        // that: none of the matches are allowlisted, so relevance ranking was replaced
        // by alphabetical order for no reason the user could see.
        //
        // Preserving the input order instead means the secondary ordering is always
        // whatever the caller already had, which is the useful answer in both modes:
        // relevance order when a query is active, and alphabetical when browsing
        // (CatalogPage sorts its catalog by name before filtering).
        const aOfficial = isOfficial(a) ? 1 : 0;
        const bOfficial = isOfficial(b) ? 1 : 0;
        return bOfficial - aOfficial;
      }

      case "topRated": {
        // Highest community rating first, lowest last. sortOrder is ignored, as for
        // "official" and "relevance".
        //
        // Ranked on the useful-vote RATIO, not the raw up-vote count: "rating" is what
        // the badge already shows ("N of M found useful"), and a count would rank a
        // 40-of-80 entry above a 12-of-12 one. Ties break toward the larger vote total,
        // since the same ratio backed by more votes is the more confident signal.
        //
        // Only entries the UI actually shows a rating for are ranked. `usefulnessDisplay`
        // is Contract 8's own gate (MIN_VOTES_FOR_RATIO = 5, #620), reused rather than
        // re-implemented so the sort and the badge can never disagree: anything reading
        // "Not enough votes yet" on the card is unranked here. Those sort LAST and are
        // never filtered out — a newly published skill must not vanish from the catalog
        // because nobody has voted on it yet.
        //
        // Ties preserve the incoming order rather than falling back to name, which is
        // the correction #591 made to "official". It matters more here: with no
        // /feedback API deployed, EVERY entry is unrated, so a name tiebreak would turn
        // this into a plain alphabetical re-sort — and would discard relevance ranking
        // outright when a search is active.
        //
        // NOTE: this is a pure usefulness sort. It is deliberately NOT #619's
        // "Recommended" score, which blends log10(activeUsers30d + 1) with this ratio
        // and substitutes a neutral 0.5 below the vote gate. #619 remains unbuilt and
        // owns that formula; see the PR description.
        const aRating = usefulnessDisplay(a.feedback);
        const bRating = usefulnessDisplay(b.feedback);
        if (aRating.kind !== "rated" && bRating.kind !== "rated") return 0;
        if (aRating.kind !== "rated") return 1;
        if (bRating.kind !== "rated") return -1;

        const aRatio = aRating.useful / aRating.total;
        const bRatio = bRating.useful / bRating.total;
        if (aRatio !== bRatio) return bRatio - aRatio;
        return bRating.total - aRating.total;
      }

      case "mostUsed": {
        // Descending by 30-day active users. sortOrder is irrelevant here for the same
        // reason as "official" above: absent adoption means "not measured", never "zero"
        // (see contract-7-adoption.ts), so an unmeasured entry can never legitimately
        // lead the list. Unmeasured entries sort LAST and are never filtered out — a
        // newly published plugin must not vanish from the browse view (#266 AC 6).
        //
        // Explicit undefined checks rather than a `?? -Infinity` sentinel: subtracting
        // two -Infinity values yields NaN, which the spec coerces to 0, so a pair of
        // unmeasured entries would compare "equal" through a NaN path.
        //
        // Ties (both unmeasured, or equal counts) preserve the incoming order rather
        // than tiebreaking by name — the same correction #591 made to "official", and
        // it matters more here. The 2026-08-10 decision was that unmeasured entries
        // "sort by name among themselves, so the tail reads exactly as the Name sort
        // would", and that outcome is unchanged when browsing: CatalogPage sorts its
        // catalog by name before filtering, so preserving that order IS name order.
        //
        // The difference is search. A name tiebreak would turn Most Used into a plain
        // alphabetical re-sort of search results, discarding relevance ranking — precisely
        // the defect #591 fixed in "official". Preserving the input order gives name order
        // while browsing and relevance order while searching, satisfying the decision in
        // the view it was made about.
        //
        // This is a PERMANENT property, not a pre-#264 workaround. With a real artifact
        // published, coverage is 42.4% (contract-7-adoption.ts), so the unmeasured MAJORITY
        // still hits the tie branch — a typical query's matches are mostly unmeasured, and a
        // name tiebreak would still scramble their relevance. Restoring the tiebreak once
        // "the data is in" would reintroduce the bug, not retire a stopgap.
        const aUsers = a.adoption?.activeUsers30d;
        const bUsers = b.adoption?.activeUsers30d;
        if (aUsers === undefined && bUsers === undefined) return 0;
        if (aUsers === undefined) return 1;
        if (bUsers === undefined) return -1;
        return bUsers - aUsers;
      }

      case "name":
        aValue = a.pluginName.toLowerCase();
        bValue = b.pluginName.toLowerCase();
        return aValue < bValue ? -1 * multiplier : aValue > bValue ? 1 * multiplier : 0;

      case "updated":
        aValue = new Date(a.lastUpdatedAt).getTime();
        bValue = new Date(b.lastUpdatedAt).getTime();
        return (aValue - bValue) * multiplier;

      case "created":
        aValue = new Date(a.firstDiscoveredAt).getTime();
        bValue = new Date(b.firstDiscoveredAt).getTime();
        return (aValue - bValue) * multiplier;

      default:
        return 0;
    }
  });

  return sorted;
}

/**
 * Invert Contract 9's artifact into `installName` → cluster id, for the collapse step.
 *
 * **Only `kind: "identity"` clusters are included.** Functional clusters describe
 * different teams' independent takes on the same job — genuine alternatives a user may
 * want to choose between — so collapsing them would hide real choice rather than remove
 * noise. Contract 9 separates the two kinds precisely so this is read off the data
 * rather than guessed at, and it means a model's judgement (which is what produces
 * functional clusters) can never HIDE an entry, only label two as related.
 *
 * A cluster with fewer than two members is skipped: it carries no information, and
 * Contract 9 says one must not be emitted in the first place.
 *
 * Lives here rather than beside `fetchSimilarity` because it is pure, and that module
 * imports the Vite-only env accessor — see the note in similarity-client.ts.
 */
export function buildDuplicateGroups(catalog: SimilarityCatalog | null): Map<string, string> {
  const map = new Map<string, string>();
  if (!catalog) return map;

  for (const cluster of catalog.clusters) {
    if (cluster.kind !== "identity") continue;
    if (!Array.isArray(cluster.members) || cluster.members.length < 2) continue;
    for (const member of cluster.members) {
      // First writer wins on the (contract-violating) case of an installName listed in
      // two clusters, so the mapping stays deterministic rather than order-dependent.
      if (member && !map.has(member)) map.set(member, cluster.id);
    }
  }
  return map;
}

/**
 * Collapse identity duplicates so one card stands for its whole cluster (#626 Part 4).
 *
 * Measured: 180 of 1,197 entries (15.2%) share a `displayName` with another entry —
 * a single-skill plugin is published twice by design (the plugin, plus its own
 * synthesized self-named skill), and a skill vendored into a second plugin appears a
 * third time. Searching `code review` returned the same `ccs-code-review` three
 * times in the top three.
 *
 * WHICH copy survives is deliberately not decided here. This runs LAST — after
 * ranking and sorting — and keeps the first member it encounters, so the survivor is
 * whichever copy the ACTIVE ordering already favours:
 *
 *   - search with signals on  -> the most-used / best-voted copy
 *   - Most Used sort          -> the most-used copy
 *   - Name sort               -> irrelevant; identity duplicates share a displayName
 *
 * That removes the need for a separate representative-selection rule, and means the
 * survivor is always consistent with what the user asked to be sorted by. (Contract 9
 * does nominate a `representative`, but it is chosen structurally without knowledge of
 * usage — the ordering here is better informed.)
 *
 * ONLY identity clusters belong in `groups`. Functional clusters — different teams'
 * independent takes on the same job — must NOT be collapsed: they are genuine
 * alternatives, and hiding them would remove real choice rather than remove noise.
 *
 * Entries with no `installName`, and any installName absent from `groups`, pass
 * through untouched. A cluster naming an entry this catalog does not contain is
 * simply never matched, so a stale artifact degrades to less collapsing rather than
 * to a wrong result.
 */
export function collapseDuplicates(
  plugins: MarketplaceEntry[],
  groups: Map<string, string>,
): MarketplaceEntry[] {
  if (groups.size === 0) return plugins;

  // Two passes: count cluster members present in THIS result set first, so the
  // surviving card can report how many entries it stands for. Counting during the
  // single pass would only ever see members encountered so far.
  const counts = new Map<string, number>();
  for (const p of plugins) {
    const g = p.installName ? groups.get(p.installName) : undefined;
    if (g) counts.set(g, (counts.get(g) ?? 0) + 1);
  }

  const kept = new Set<string>();
  const out: MarketplaceEntry[] = [];
  for (const p of plugins) {
    const g = p.installName ? groups.get(p.installName) : undefined;
    if (!g) {
      out.push(p);
      continue;
    }
    if (kept.has(g)) continue; // a later copy of an already-kept cluster
    kept.add(g);
    const n = counts.get(g) ?? 1;
    // Leave duplicateCount off when the cluster has only one member visible here —
    // filters can reduce a cluster to a single entry, and "1 version" is noise.
    out.push(n > 1 ? { ...p, duplicateCount: n } : p);
  }
  return out;
}

/**
 * Combined search and filter
 */
export function filterPlugins(
  plugins: MarketplaceEntry[],
  options: SearchOptions,
  isOfficial: IsOfficial = () => false,
): MarketplaceEntry[] {
  let results = plugins;

  // Search by query
  if (options.query) {
    if (options.semanticHits?.length) {
      // ── "Nothing here does this" ──
      // Retrieval ranks the whole catalog and slices, so without this it can never return
      // empty: `order lunch for the team` produced 124 results, all irrelevant (measured 0 of
      // 9 correct rejections on the golden set).
      //
      // The gate is the best COSINE, not the fused score. RRF is rank-derived, so its numbers
      // sit in the same narrow band whether results are excellent or worthless; cosine over
      // unit-normalised vectors is absolute and comparable across queries. (Same reason
      // Elasticsearch documents `min_score` as unreliable for BM25 while vector stores treat
      // similarity floors as ordinary practice.)
      //
      // Measured separation on the 50-case golden set — no overlap:
      //   8 no-answer cases   best cosine max 0.213
      //   42 answerable cases best cosine min 0.303
      // Every floor in 0.22–0.30 gives 8/8 correct rejections and 0/42 false rejections, so
      // 0.25 sits mid-band with margin on both sides.
      //
      // Safe for exact-name queries because the entry's NAME is part of the embedded text, so
      // typing a name verbatim always scores well against that entry (observed min for any
      // answerable case: 0.303). A rename of the embedding input would need this re-measured.
      const bestCosine = Math.max(...options.semanticHits.map((h) => h.score));
      if (bestCosine < SEMANTIC_REJECT_FLOOR) return [];
      // Hybrid: one ranking, fused by rank. Guarded on `options.query` because semantic hits
      // are query-derived — with no query there is nothing to fuse them into.
      // This field list must stay in sync with SearchTuning: `options` is a SearchOptions
      // superset, so it cannot be spread wholesale into a SearchTuning param. A new tuning
      // field added upstream needs adding here too — the same drift the `{ ...tuning }` spread
      // in the auto-mode branch above guards against (where `boosts` was once lost).
      const lexical = searchPluginsScored(results, options.query, {
        mode: options.mode,
        useSignals: options.useSignals,
        boosts: options.boosts,
      });
      // No arbitrary truncation here: how many results to SHOW is a separate, relevance-based
      // decision, not a side effect of retrieval depth. Fuse everything both engines found and
      // let ordering (and any downstream confidence trim) decide what the user sees.
      const limit = lexical.length + options.semanticHits.length;
      results = fuseRRF(
        lexical,
        options.semanticHits,
        indexByInstallName(results),
        limit,
        // Chosen from the query's shape unless a measurement explicitly overrides it.
        options.rrfWeights ?? weightsForQuery(options.query),
      ).map((c) => c.entry);
    } else {
      results = searchPlugins(results, options.query, {
        mode: options.mode,
        useSignals: options.useSignals,
        boosts: options.boosts,
      });
    }

    // Last word on ordering, for BOTH paths: if the user typed an entry's name, that entry is
    // first. BM25 cannot be trusted to do this — its length normalisation ranked `acc` above
    // `acc.run` for the query `acc.run`. See promoteExactNameMatches.
    results = promoteExactNameMatches(results, options.query);
  }

  // Filter by supported clients
  if (options.supportedClients && options.supportedClients.length > 0) {
    results = filterBySupportedClients(results, options.supportedClients);
  }

  // Filter by categories
  if (options.categories && options.categories.length > 0) {
    results = filterByCategories(results, options.categories);
  }

  // Filter by extension type (plugin vs skill)
  if (options.extensionType) {
    results = filterByExtensionType(results, options.extensionType);
  }

  // Narrow to Official CAI allowlist
  if (options.officialOnly) {
    results = results.filter(isOfficial);
  }

  // Sort. "relevance" is skipped because it has no comparator: the order to keep is
  // the one searchPlugins already produced. Every other ordering applies whether or
  // not a query is active.
  //
  // This used to be `!options.query.trim()` — sorting was dropped entirely the moment
  // anything was typed, so picking a sort mid-search silently did nothing (#590). The
  // intent was to protect relevance order, which is still protected, but now by asking
  // "is relevance the chosen ordering?" instead of "is a query present?". The two
  // questions only look the same while relevance isn't selectable.
  //
  // With no query, "relevance" leaves the input order untouched — there is nothing to
  // be relevant to. The toolbar only offers it while a query is active for that reason.
  if (options.sortBy && options.sortBy !== "relevance") {
    results = sortPlugins(results, options.sortBy, options.sortOrder, isOfficial);
  }

  // LAST, deliberately — collapsing after ordering makes the surviving copy the one
  // the active ordering already favours. See collapseDuplicates.
  if (options.duplicateGroups) {
    results = collapseDuplicates(results, options.duplicateGroups);
  }

  return results;
}

/**
 * Debounce utility for search input
 */
export type DebouncedFn<T extends (...args: never[]) => void> = ((...args: Parameters<T>) => void) & {
  cancel: () => void;
};

export function debounce<T extends (...args: never[]) => void>(
  func: T,
  delayMs: number = 300
): DebouncedFn<T> {
  let timeoutId: ReturnType<typeof setTimeout>;

  const debounced = function debounced(...args: Parameters<T>) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func(...args);
    }, delayMs);
  } as DebouncedFn<T>;

  debounced.cancel = () => {
    clearTimeout(timeoutId);
  };

  return debounced;
}
